import { isNil } from '@sixfold/typed-primitives';
import * as React from 'react';

export interface AuthService {
  authenticate(service_id_token: string): Promise<{ isLoggedIn: boolean }>;
}

export interface LoginChildProps {
  error?: string;
  loading: boolean;
  refLoginButton: (element: HTMLElement | null) => void;
}

export interface LoginWrapperProps {
  google_client_id: string;
  authService: AuthService;
  children?: React.ReactNode | ((props: LoginChildProps) => React.ReactNode);
}

export const LoginWrapper: React.FC<LoginWrapperProps> = ({ google_client_id, authService, children }) => {
  const buttonRef = React.useRef<HTMLElement | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);

  const refLoginButton = React.useCallback((element: HTMLElement | null) => {
    buttonRef.current = element;
  }, []);

  React.useEffect(() => {
    const button = buttonRef.current;
    if (isNil(button) || google_client_id.trim() === '') {
      return;
    }

    google.accounts.id.initialize({
      client_id: google_client_id,
      callback: async (response) => {
        setLoading(true);

        try {
          await authService.authenticate(response.credential);
          setError(undefined);
          setLoading(false);
        } catch (e: unknown) {
          const err = (e as { error?: string })?.error ?? 'login_failed';
          setLoading(false);
          setError(err);
        }
      },
    });

    google.accounts.id.renderButton(button, { type: 'standard', theme: 'outline', size: 'large' });
  }, [google_client_id, authService]);

  return (
    <div>
      {typeof children === 'function'
        ? children({
            error,
            loading,
            refLoginButton,
          })
        : children}
    </div>
  );
};
